import {defineStore} from 'pinia';
import {useUserStore} from '~/store/user';
import {useConfigurationStore} from '~/store/configuration';
import {normalizeTenantId} from '~/utils/tenantId';

export const COMPANY_FILTER_USER_SETTING_KEY = 'COMPANY_FILTER';

export const useCompanyFilterStore = defineStore('companyFilter', () => {
  const userStore = useUserStore();
  const configurationStore = useConfigurationStore();

  const selectedTenantIds = ref<string[]>([]);
  const appliedTenantIds = ref<string[]>([]);
  const loadedOrganisationId = ref<string | null>(null);
  const isLoading = ref(false);
  const loadFailed = ref(false);
  const shouldFilter = ref(false);

  let loadRequestId = 0;

  // Last selection successfully persisted; used to roll back the UI when a save fails.
  let persistedTenantIds: string[] = [];

  let loadPromise: Promise<void> | null = null;
  let loadPromiseOrganisationId: string | null = null;

  const orgSerialNumbers = computed<string[]>(() =>
    userStore.serialNumbers.filter((serial): serial is string => Boolean(serial))
  );

  const isSelected = (tenantId: string): boolean => {
    const normalizedTenantId = normalizeTenantId(tenantId);
    return selectedTenantIds.value.some(id => normalizeTenantId(id) === normalizedTenantId);
  };

  const isAppliedSelected = (tenantId: string): boolean => {
    const normalizedTenantId = normalizeTenantId(tenantId);
    return appliedTenantIds.value.some(id => normalizeTenantId(id) === normalizedTenantId);
  };

  const allCompaniesSelected = computed<boolean>(() =>
    orgSerialNumbers.value.length > 0 && orgSerialNumbers.value.every(isSelected)
  );

  const allAppliedCompaniesSelected = computed<boolean>(() =>
    orgSerialNumbers.value.length > 0 && orgSerialNumbers.value.every(isAppliedSelected)
  );

  // Missing or stale selections use the full organisation scope; failed loads stay closed.
  const effectiveSerialNumbers = computed<string[]>(() => {
    if (loadFailed.value) return [];

    const effective = orgSerialNumbers.value.filter(isAppliedSelected);

    return effective.length > 0 ? effective : orgSerialNumbers.value;
  });

  const intersectWithOrganisation = (tenantIds: string[]): string[] => {
    const requested = new Set(tenantIds.map(normalizeTenantId));

    return orgSerialNumbers.value.filter(serial => requested.has(normalizeTenantId(serial)));
  };

  const parseStoredSelection = (value: string | null | undefined): string[] | null => {
    if (!value) return null;

    try {
      const parsed = JSON.parse(value);

      return Array.isArray(parsed) ? parsed.map(String) : null;
    } catch {
      return null;
    }
  };

  const setSelectedTenantIds = (tenantIds: string[]) => {
    selectedTenantIds.value = intersectWithOrganisation(tenantIds);
  };

  const syncDraftFromApplied = () => {
    selectedTenantIds.value = [...appliedTenantIds.value];
    shouldFilter.value = false;
  };

  const discardDraft = () => {
    syncDraftFromApplied();
  };

  const applySelection = async (): Promise<boolean> => {
    const organisationId = userStore.activeOrganisation;
    if (!organisationId) return false;

    const snapshot = [...selectedTenantIds.value];
    const value = allCompaniesSelected.value ? null : JSON.stringify(snapshot);
    const saved = await configurationStore.saveUserSetting(COMPANY_FILTER_USER_SETTING_KEY, value, organisationId, true);

    if (saved && userStore.activeOrganisation === organisationId) {
      appliedTenantIds.value = snapshot;
      persistedTenantIds = [...snapshot];
      shouldFilter.value = false;
      return true;
    }

    selectedTenantIds.value = [...persistedTenantIds];
    return false;
  };

  const toggleTenant = (tenantId: string) => {
    shouldFilter.value = true;
    if (isSelected(tenantId)) {
      const normalizedTenantId = normalizeTenantId(tenantId);
      setSelectedTenantIds(selectedTenantIds.value.filter(id => normalizeTenantId(id) !== normalizedTenantId));
      return;
    }

    setSelectedTenantIds([...selectedTenantIds.value, tenantId]);
  };

  const selectAll = () => {
    if (allCompaniesSelected.value) return;

    shouldFilter.value = true;
    setSelectedTenantIds([...orgSerialNumbers.value]);
  };

  // Restore the saved selection before the first item fetch, and dedupe concurrent/repeat
  // calls so a hard refresh doesn't fall back to "all companies" while the setting loads.
  const loadForActiveOrganisation = (): Promise<void> => {
    const organisationId = userStore.activeOrganisation;

    if (!organisationId) return Promise.resolve();
    if (loadedOrganisationId.value === organisationId) {
      loadFailed.value = false;
      return Promise.resolve();
    }
    if (loadPromise && loadPromiseOrganisationId === organisationId) return loadPromise;

    const requestId = ++loadRequestId;
    loadPromiseOrganisationId = organisationId;

    loadPromise = (async () => {
      isLoading.value = true;
      loadFailed.value = false;

      try {
        const setting = await configurationStore.getUserOrganizationSettings(COMPANY_FILTER_USER_SETTING_KEY, organisationId, true);
        if (requestId !== loadRequestId || userStore.activeOrganisation !== organisationId) return;

        const stored = parseStoredSelection(setting?.keyValue);
        const restored = stored ? intersectWithOrganisation(stored) : [];
        const resolvedSelection = restored.length > 0 ? restored : [...orgSerialNumbers.value];

        selectedTenantIds.value = resolvedSelection;
        appliedTenantIds.value = [...resolvedSelection];
        shouldFilter.value = false
        loadedOrganisationId.value = organisationId;
        persistedTenantIds = [...resolvedSelection];
      } catch (error) {
        if (requestId !== loadRequestId || userStore.activeOrganisation !== organisationId) return;

        // A read failure must not widen the selection or mark the organisation loaded; leave prior
        // state intact so a retry can recover instead of silently showing every company.
        console.error('Failed to load company filter setting:', error);
        loadFailed.value = true;
      } finally {
        if (requestId === loadRequestId) {
          isLoading.value = false;
          loadPromise = null;
          loadPromiseOrganisationId = null;
        }
      }
    })();

    return loadPromise;
  };

  const reset = () => {
    selectedTenantIds.value = [];
    appliedTenantIds.value = [];
    persistedTenantIds = [];
    loadedOrganisationId.value = null;
    isLoading.value = false;
    loadFailed.value = false;
    shouldFilter.value = false;
    loadRequestId++;
    loadPromise = null;
    loadPromiseOrganisationId = null;
  };

  return {
    selectedTenantIds,
    appliedTenantIds,
    loadedOrganisationId,
    isLoading,
    loadFailed,
    orgSerialNumbers,
    effectiveSerialNumbers,
    allCompaniesSelected,
    allAppliedCompaniesSelected,
    shouldFilter,
    isSelected,
    setSelectedTenantIds,
    toggleTenant,
    selectAll,
    syncDraftFromApplied,
    discardDraft,
    applySelection,
    loadForActiveOrganisation,
    reset
  };
});